import React, { useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import config from "../Config";
import { HandleResponse } from "../functions/HandleResponse";
import { authFetch } from "../functions/authFetch";
import { useNavigate } from "react-router-dom";
import "./pages.css";

const DeveloperAccess = () => {
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [revealedKey, setRevealedKey] = useState(null); // { name, api_key }
  const [revoking, setRevoking] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const token = Cookies.get("runner_app");
      const response = await authFetch(
        `${config.AUTH_API}/v1/apikey/get-all-api-keys`,
        {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        },
      );
      await HandleResponse(response, navigate);
      if (response.ok) {
        const data = await response.json();
        setKeys(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to fetch API keys:", err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = newKeyName.trim();
    if (!name) return;
    setCreateError(null);
    setCreating(true);
    try {
      const token = Cookies.get("runner_app");
      const response = await authFetch(
        `${config.AUTH_API}/v1/apikey/create-api-key?name=${encodeURIComponent(name)}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      );
      await HandleResponse(response, navigate);
      const data = await response.json();
      if (response.ok) {
        setRevealedKey({ name: data.name, api_key: data.api_key });
        setNewKeyName("");
        fetchKeys();
      } else {
        setCreateError(data.detail || "Failed to create API key.");
      }
    } catch (err) {
      setCreateError(err.message || "Something went wrong.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (name) => {
    setRevoking(name);
    setConfirmRevoke(null);
    try {
      const token = Cookies.get("runner_app");
      const response = await authFetch(
        `${config.AUTH_API}/v1/apikey/remove-api-key?name=${encodeURIComponent(name)}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
      );
      await HandleResponse(response, navigate);
      if (response.ok) {
        setKeys((prev) => prev.filter((k) => k.name !== name));
      }
    } catch (err) {
      console.error("Failed to revoke key:", err);
    } finally {
      setRevoking(null);
    }
  };

  const handleCopy = () => {
    if (!revealedKey) return;
    navigator.clipboard.writeText(revealedKey.api_key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatDate = (raw) => {
    if (!raw) return "Never";
    try {
      return new Date(raw).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      });
    } catch { return raw; }
  };

  return (
    <section className="acct-card">
      <div className="acct-card-header">
        <h2>Developer Access</h2>
      </div>
      <div className="acct-card-body">

        {/* ── Create form ── */}
        <form onSubmit={handleCreate} className="dev-key-create-form">
          <div className="dev-key-create-row">
            <input
              type="text"
              className="dev-key-name-input"
              placeholder="Key name (e.g. CI pipeline)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              disabled={creating}
              maxLength={64}
            />
            <button type="submit" className="dev-key-create-btn" disabled={creating || !newKeyName.trim()}>
              {creating ? "Creating…" : "Generate Key"}
            </button>
          </div>
          {createError && <div className="dev-key-error">{createError}</div>}
        </form>

        {/* ── Newly created key reveal ── */}
        {revealedKey && (
          <div className="dev-key-reveal">
            <div className="dev-key-reveal-title">
              <span className="dev-key-reveal-icon">🔑</span>
              Key created: <strong>{revealedKey.name}</strong>
            </div>
            <p className="dev-key-reveal-warning">
              Copy this key now — it will not be shown again.
            </p>
            <div className="dev-key-reveal-row">
              <code className="dev-key-value">{revealedKey.api_key}</code>
              <button className="dev-key-copy-btn" onClick={handleCopy}>
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <button className="dev-key-dismiss-btn" onClick={() => { setRevealedKey(null); setCopied(false); }}>
              Done
            </button>
          </div>
        )}

        {/* ── Key list ── */}
        <div className="dev-key-list">
          {loading && <div className="dev-key-empty">Loading…</div>}
          {!loading && keys.length === 0 && (
            <div className="dev-key-empty">No active API keys. Generate one above.</div>
          )}
          {!loading && keys.map((key) => (
            <div key={key.name} className="dev-key-item">
              <div className="dev-key-info">
                <span className="dev-key-item-name">{key.name}</span>
                <span className="dev-key-item-prefix">{key.prefix}…</span>
              </div>
              <div className="dev-key-meta">
                <span>Created {formatDate(key.created_at)}</span>
                <span>Last used {formatDate(key.last_used_at)}</span>
              </div>
              <button
                className="dev-key-revoke-btn"
                onClick={() => setConfirmRevoke(key.name)}
                disabled={revoking === key.name}
              >
                {revoking === key.name ? "Revoking…" : "Revoke"}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Revoke confirmation popup ── */}
      {confirmRevoke && (
        <div className="popup">
          <div className="popup-inner">
            <span className="popup-icon popup-error">!</span>
            <h2>Revoke Key</h2>
            <p>Revoke <strong>{confirmRevoke}</strong>? Any integrations using it will stop working immediately.</p>
            <div className="mcr-popup-buttons">
              <button onClick={() => setConfirmRevoke(null)}>Cancel</button>
              <button onClick={() => handleRevoke(confirmRevoke)}>Revoke</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DeveloperAccess;
